import type { UserRoute } from "../lib/routes.ts"
import { isMainTab, MainTabHeading } from "./main-tab-heading.tsx"

const loadingCopy: Readonly<
  Record<
    string,
    {
      readonly title: string
      readonly description: string
    }
  >
> = {
  "APP-HOME-001": { title: "홈", description: "사용 가능한 잔액과 최근 이용 내역을 확인하세요." },
  "APP-BAL-001": { title: "내 잔액", description: "지금 사용할 수 있는 금액과 소멸 예정 잔액을 확인하세요." },
  "APP-BAL-002": { title: "이용 내역", description: "전환과 사용 내역을 시간순으로 확인하세요." },
  "APP-SHOP-001": { title: "사용하기", description: "현재 교환 가능한 상품을 확인하세요." },
  "APP-SHOP-002": { title: "사용하기", description: "현재 교환 가능한 상품을 확인하세요." },
  "APP-MY-001": { title: "내 정보", description: "계정과 이용 설정을 관리하세요." },
  "APP-MY-003": { title: "환경 설정", description: "알림 수신 설정을 관리하세요." },
  "APP-NOTI-001": { title: "알림", description: "전환과 상품 처리 소식을 확인하세요." },
  "APP-ORD-001": { title: "주문 내역", description: "상품 교환 처리 상태를 확인하세요." },
  "APP-VCH-001": { title: "내 상품권", description: "발급된 상품권의 상태와 유효기간을 확인하세요." },
  "APP-CS-001": { title: "고객지원", description: "문의 상태와 답변을 확인하세요." },
  "APP-CS-002": { title: "문의 상세", description: "문의 상태와 답변을 확인하세요." },
  "APP-DEL-001": { title: "계정 삭제", description: "남은 잔액이 0원이고 진행 중인 거래가 없을 때 계정을 삭제할 수 있습니다. 거래 원장과 법정 보존 기록은 관련 기간 동안 분리 보관됩니다." },
}
export const journeyLoadingHeading = (
  route: UserRoute,
): {
  readonly title: string
  readonly description: string
} => loadingCopy[route.id] ?? route
const Skeleton = ({ shape = "line" }: { readonly shape?: "line" | "short" | "text" | "title" | "amount" | "media" | "control" | "avatar" }) => <span aria-hidden="true" className={`journey-skeleton journey-skeleton-${shape}`} />
const skeletonSlots = ["first", "second", "third", "fourth", "fifth", "sixth"] as const
const SkeletonRows = ({ count = 3 }: { readonly count?: number }) => (
  <div aria-hidden="true" className="journey-skeleton-rows">
    {skeletonSlots.slice(0, count).map((slot) => (
      <div className="journey-skeleton-row" key={slot}>
        <Skeleton shape="short" />
        <Skeleton shape="short" />
      </div>
    ))}
  </div>
)
const ProductSkeletons = ({ count, compact = false }: { readonly count: number; readonly compact?: boolean }) =>
  skeletonSlots.slice(0, count).map((slot) => (
    <article aria-hidden="true" className="consumer-product-card" key={slot}>
      <div className="consumer-product-card-link">
        <div className="consumer-product-media">
          <Skeleton shape="media" />
        </div>
        <div className="consumer-product-copy">
          <strong className="consumer-product-name">
            {compact ? null : <Skeleton shape="text" />}
            <Skeleton shape="text" />
          </strong>
          <div className="consumer-product-meta">
            <strong className="consumer-product-price">
              <Skeleton shape="text" />
            </strong>
          </div>
        </div>
      </div>
    </article>
  ))
const CheckoutLoading = ({ heading }: { readonly heading?: string }) => (
  <section className="todo26-panel">
    <header className="todo26-panel-heading">
      <h2>{heading ?? <Skeleton shape="title" />}</h2>
    </header>
    <SkeletonRows />
    <Skeleton shape="control" />
    <Skeleton shape="control" />
  </section>
)
const RedesignHomeLoading = () => (
  <div className="home-layout redesign-home-layout" data-commerce="true" data-home-design="redesign">
    <section className="redesign-home-balance">
      <div className="redesign-home-balance-heading">
        <p>사용 가능한 통합 잔액</p>
        <span aria-hidden="true" className="redesign-home-balance-detail">
          <Skeleton shape="text" />
        </span>
      </div>
      <strong className="redesign-home-balance-amount">
        <Skeleton shape="text" />
      </strong>
      <span aria-hidden="true" className="redesign-home-import">
        <Skeleton shape="text" />
      </span>
    </section>
    <section className="home-featured">
      <header className="home-section-head">
        <h2>이번 주 인기</h2>
      </header>
      <div className="home-featured-rail">
        <ProductSkeletons compact count={4} />
      </div>
    </section>
    <section className="redesign-home-intro">
      <header className="home-section-head">
        <h2>포인트를 전환해서 사용해 보세요</h2>
      </header>
      <div aria-hidden="true" className="redesign-home-intro-card">
        <span className="redesign-home-gift">
          <Skeleton shape="avatar" />
        </span>
        <div>
          <h3>인기 상품권으로 바꾸기</h3>
          <p>
            <Skeleton shape="text" />
            <Skeleton shape="text" />
          </p>
        </div>
      </div>
    </section>
    <section className="recent-activity redesign-home-activity">
      <header className="home-section-head">
        <h2>최근 이용 내역</h2>
      </header>
      <RedesignHistoryLoading />
    </section>
  </div>
)
const RedesignCatalogLoading = () => (
  <div className="catalog-layout catalog-layout-commerce catalog-layout-redesign" data-commerce-catalog>
    <label aria-hidden="true" className="catalog-search-commerce">
      <span className="catalog-search-icon">
        <Skeleton shape="text" />
      </span>
      <input aria-label="상품 검색" className="redesign-loading-input" disabled type="search" />
    </label>
    <div aria-hidden="true" className="catalog-brand-navigation">
      <div className="catalog-chips journey-skeleton-chips">
        {skeletonSlots.slice(0, 4).map((slot) => (
          <button disabled key={slot} tabIndex={-1} type="button">
            <Skeleton shape="text" />
          </button>
        ))}
      </div>
    </div>
    <label aria-hidden="true" className="catalog-affordable-filter">
      <input disabled type="checkbox" />
      <span>
        <Skeleton shape="text" />
      </span>
      <small>
        <Skeleton shape="text" />
      </small>
    </label>
    <div aria-hidden="true" className="catalog-sort-row" data-vivid-catalog-toolbar>
      <p>
        <Skeleton shape="text" />
      </p>
      <span className="catalog-sort-trigger">
        <Skeleton shape="text" />
      </span>
    </div>
    <div className="consumer-product-grid product-grid-commerce">
      <ProductSkeletons count={6} />
    </div>
  </div>
)
const RedesignHistoryLoading = () => (
  <div aria-hidden="true" className="redesign-history-layout">
    <ol className="redesign-history-list">
      {skeletonSlots.slice(0, 4).map((slot) => (
        <li key={slot}>
          <div className="redesign-history-row">
            <Skeleton shape="short" />
            <span className="redesign-history-description">
              <Skeleton shape="text" />
            </span>
            <strong>
              <Skeleton shape="text" />
            </strong>
          </div>
        </li>
      ))}
    </ol>
  </div>
)
const RedesignBalanceLoading = () => (
  <div className="redesign-balance-layout" data-redesign-balance>
    <section className="redesign-balance-total">
      <p>사용 가능한 통합 잔액</p>
      <strong>
        <Skeleton shape="text" />
      </strong>
    </section>
    <section className="redesign-balance-expiry">
      <span>소멸 예정</span>
      <div className="redesign-expiry-amounts">
        {["D-30", "D-90"].map((label) => (
          <span key={label}>
            <small>{label}</small>
            <strong>
              <Skeleton shape="text" />
            </strong>
          </span>
        ))}
      </div>
    </section>
    <dl className="redesign-balance-details">
      {["사용 가능 잔액", "보류 금액"].map((label) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>
            <Skeleton shape="text" />
          </dd>
        </div>
      ))}
    </dl>
    <div aria-hidden="true" className="redesign-balance-notes">
      <p>
        <Skeleton shape="text" />
      </p>
      <p>
        <Skeleton shape="text" />
      </p>
    </div>
    <section className="redesign-balance-history">
      <header>
        <h2>이용 내역</h2>
        <p>
          <Skeleton shape="text" />
        </p>
      </header>
      <RedesignHistoryLoading />
    </section>
  </div>
)
const RedesignMyLoading = () => (
  <nav aria-label="내 정보 바로가기" className="redesign-my-layout">
    <header className="redesign-my-profile">
      <div className="redesign-my-customer">
        <span>고객 번호</span>
        <div className="customer-number-row">
          <span className="customer-number-value">
            <Skeleton shape="text" />
          </span>
          <span aria-hidden="true" className="customer-number-copy" />
        </div>
      </div>
    </header>
    <div className="redesign-my-menu-groups">
      {[
        { title: "계정", rows: 2 },
        { title: "이용 내역", rows: 2 },
        { title: "알림 및 설정", rows: 2 },
        { title: "지원 및 보안", rows: 1 },
      ].map(({ title, rows }) => (
        <section className="redesign-my-menu-group" key={title}>
          <h2>{title}</h2>
          <ul aria-hidden="true">
            {skeletonSlots.slice(0, rows).map((slot) => (
              <li key={slot}>
                <div className="redesign-loading-my-row">
                  <Skeleton shape="text" />
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  </nav>
)
const RedesignNotificationsLoading = () => (
  <div className="redesign-notification-layout" data-notification-layout>
    <header className="redesign-notification-intro">
      <h2>알림</h2>
      <p>
        <Skeleton shape="text" />
      </p>
    </header>
    <div aria-hidden="true" className="redesign-loading-notifications">
      {skeletonSlots.slice(0, 3).map((slot) => (
        <div className="redesign-loading-notification" key={slot}>
          <strong>
            <Skeleton shape="text" />
          </strong>
          <p>
            <Skeleton shape="text" />
          </p>
          <small>
            <Skeleton shape="text" />
          </small>
        </div>
      ))}
    </div>
  </div>
)
const RedesignProductLoading = () => (
  <div className="product-checkout-layout product-checkout-redesign">
    <section className="product-detail product-detail-redesign">
      <div aria-hidden="true" className="redesign-product-hero">
        <Skeleton shape="media" />
      </div>
      <div className="redesign-product-body">
        <div className="redesign-product-intro">
          <h2>
            <Skeleton shape="text" />
          </h2>
        </div>
        <div className="redesign-product-amount">
          <span>교환 금액</span>
          <strong>
            <Skeleton shape="text" />
          </strong>
        </div>
        <dl className="redesign-product-facts">
          {["발송", "받는 번호", "교환 후 잔액"].map((label) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>
                <Skeleton shape="text" />
              </dd>
            </div>
          ))}
        </dl>
        <section className="redesign-product-notice">
          <h3>유의사항</h3>
          <ul aria-hidden="true">
            {skeletonSlots.slice(0, 3).map((slot) => (
              <li key={slot}>
                <Skeleton shape="text" />
                <Skeleton shape="text" />
              </li>
            ))}
          </ul>
        </section>
      </div>
    </section>
    <section aria-hidden="true" className="todo26-panel" data-todo26-checkout>
      <div className="action-area">
        <div className="checkout-consent">
          <Skeleton shape="text" />
          <Skeleton shape="text" />
        </div>
        <span className="button">
          <Skeleton shape="text" />
        </span>
      </div>
    </section>
  </div>
)
export const JourneyLoadingBody = ({ route }: { readonly route: UserRoute }) => {
  if (route.id === "APP-HOME-001") return <RedesignHomeLoading />
  if (route.id === "APP-SHOP-001" || route.id === "APP-SHOP-002") return <RedesignCatalogLoading />
  if (route.id === "APP-MY-001") return <RedesignMyLoading />
  if (route.id === "APP-SHOP-003") return <RedesignProductLoading />
  if (route.id === "APP-BAL-001") return <RedesignBalanceLoading />
  if (route.id === "APP-BAL-002") return <RedesignHistoryLoading />
  if (route.id === "APP-NOTI-001") return <RedesignNotificationsLoading />
  if (route.id === "APP-RDM-001") return <CheckoutLoading heading="최종 확인" />
  if (route.kind === "redemption") return <CheckoutLoading />
  if (route.id === "APP-CS-001")
    return (
      <div data-support-layout>
        <SkeletonRows />
        <SkeletonRows />
      </div>
    )
  if (route.id === "APP-CS-002")
    return (
      <div data-support-case>
        <SkeletonRows />
      </div>
    )
  return <SkeletonRows count={4} />
}
export const JourneyLoading = ({ route }: { readonly route: UserRoute }) => {
  const heading = journeyLoadingHeading(route)
  const isProduct = route.id === "APP-SHOP-003"
  const isTodo26 = isProduct || route.kind === "redemption"
  const viewKind = route.id === "APP-HOME-001" ? "home" : route.id === "APP-SHOP-001" || route.id === "APP-SHOP-002" ? "catalog" : undefined
  return (
    <article aria-busy="true" className={`journey-card journey-loading${isTodo26 ? " todo26-journey" : ""}`} data-loading-design="redesign" data-route-id={route.id} data-route-kind={route.kind} data-todo26-screen={isTodo26 ? "loading" : undefined} data-view-kind={viewKind}>
      <output className="visually-hidden">화면을 불러오고 있습니다.</output>
      {isMainTab(route.id) ? (
        <MainTabHeading routeId={route.id} />
      ) : isProduct ? null : (
        <header className="journey-heading">
          <h1>{heading.title}</h1>
          <p className="phrase-safe-block">{heading.description}</p>
        </header>
      )}
      <JourneyLoadingBody route={route} />
    </article>
  )
}
